import React from "react"
import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup"
import { Navigate, useSearchParams } from "react-router-dom"
import { useInvitation } from "../../features/invitation/hooks"
import { Controller, useForm } from "react-hook-form"
import {
  EmploymentType,
  EmploymentTypeLabels,
  EmploymentTypes,
  Gender,
  GenderLabels,
  Genders,
  PROVINCES,
} from "src/modules/const"
import { Label } from "src/components/ui/label"
import { Input } from "src/components/ui/input"
import { RadioGroup, RadioGroupItem } from "src/components/ui/radio-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select"
import DatePicker from "src/components/DatePicker"
import { Button } from "src/components/ui/button"
import { CheckIcon, CircleAlertIcon, LoaderIcon } from "lucide-react"
import { useAuth } from "src/providers/LoginProvider"
import { ApiError } from "src/openapi"

export default function EmployeeSignup() {
  const [searchParams] = useSearchParams()
  const code = searchParams.get("code") || ""

  if (!code) {
    return <Navigate to="/" replace />
  }

  return <EmployeeSignupContent code={code} />
}

type EmployeeSignupContentProps = {
  code: string
}

function EmployeeSignupContent({ code }: EmployeeSignupContentProps) {
  const { lineIdToken } = useAuth()

  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [submissionCompleted, setSubmissionCompleted] = React.useState(false)
  const [signupError, setSignupError] = React.useState<string | null>(null)

  const { companyName, listMigrationUrl, signup } = useInvitation(code)

  const schema = yup.object({
    invitationCode: yup.string().required(),
    idToken: yup.string().required(),
    lastName: yup.string().required("姓は必須です").max(63, "最大63文字までです"),
    firstName: yup.string().required("名は必須です").max(63, "最大63文字までです"),
    lastNameKana: yup
      .string()
      .required("姓（フリガナ）は必須です")
      .matches(/^[ァ-ヶー]+$/, "全角カタカナで入力してください")
      .max(63, "最大63文字までです"),
    firstNameKana: yup
      .string()
      .required("名（フリガナ）は必須です")
      .matches(/^[ァ-ヶー]+$/, "全角カタカナで入力してください")
      .max(63, "最大63文字までです"),
    employmentType: yup.string().required("雇用形態は必須です"),
    gender: yup.mixed<Gender>().oneOf(Object.values(Genders)),
    email: yup.string().email().max(127, "最大127文字までです"),
    birthday: yup.string().matches(/^\d{4}-\d{2}-\d{2}$/),
    postCode: yup.string().matches(/^\d{3}-\d{4}$/, "郵便番号は000-0000の形式で入力してください"),
    province: yup.string().max(31, "最大31文字までです"),
    city: yup.string().max(63, "最大63文字までです"),
    address: yup.string().max(1023, "最大1023文字までです"),
    buildingNo: yup.string().max(127, "最大127文字までです"),
    phoneNumber: yup
      .string()
      .matches(/^\d{2,4}-\d{2,4}-\d{4}$/, "電話番号は000-000-0000の形式で入力してください"),
    joiningDate: yup
      .string()
      .required("入社日は必須です")
      .matches(/^\d{4}-\d{2}-\d{2}$/),
  })
  type SignupFormValues = yup.InferType<typeof schema>

  const {
    control,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm<SignupFormValues>({
    resolver: yupResolver(schema),
    values: {
      invitationCode: code,
      idToken: lineIdToken || "",
      lastName: "",
      firstName: "",
      lastNameKana: "",
      firstNameKana: "",
      joiningDate: "",
      employmentType: EmploymentTypes.SIDE,
    },
  })

  const submit = async (data: SignupFormValues) => {
    if (!isValid) {
      return
    }
    if (!lineIdToken) {
      return
    }
    setIsSubmitting(true)
    try {
      await signup(data)
      setSubmissionCompleted(true)
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 400) {
          setSignupError(e.body)
        } else {
          setSignupError("時間をおいて再度お試しください。")
        }
      } else {
        setSignupError("時間をおいて再度お試しください。")
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  if (listMigrationUrl === undefined) {
    return <EmployeeSignupLoading />
  }

  if (listMigrationUrl) {
    return (
      <div className="w-full h-full flex flex-col gap-4">
        <div className="w-full h-[50px] flex justify-center items-center sticky top-0 bg-white p-4">
          <img src="/triple_logo.png" alt="logo" className="h-4" />
        </div>
        <div className="w-full flex flex-col justify-center items-center gap-4 p-4">
          <p className="text-lg font-bold">{companyName}</p>
          <p className="text-md">TRIPLE LIST の登録画面から続けてください。</p>
          <a className="text-sm text-[#00a080] underline" href={listMigrationUrl}>
            TRIPLE LIST の登録画面を開く
          </a>
        </div>
      </div>
    )
  }

  if (window.location.hostname !== "localhost" && !lineIdToken) {
    return (
      <div className="w-full h-full flex flex-col gap-4">
        <div className="w-full h-[50px] flex justify-center items-center sticky top-0 bg-white p-4">
          <img src="/triple_logo.png" alt="logo" className="h-4" />
        </div>
        <div className="w-full flex flex-col justify-center items-center gap-4 p-4">
          <p className="text-md">LINEミニアプリからアクセスしてください。</p>
        </div>
      </div>
    )
  }

  if (signupError) {
    return (
      <div className="w-full h-full flex flex-col gap-4">
        <div className="w-full h-[50px] flex justify-center items-center sticky top-0 bg-white p-4">
          <img src="/triple_logo.png" alt="logo" className="h-4" />
        </div>
        <div className="w-full flex flex-col justify-center items-center gap-4 p-4">
          <p className="text-lg font-bold">{companyName}</p>
          <CircleAlertIcon className="w-16 h-16 text-manual-red bg-white rounded-full" />
          <p className="text-md">登録中にエラーが発生しました。</p>
          <p className="text-md">{signupError}</p>
        </div>
      </div>
    )
  }

  if (submissionCompleted) {
    return (
      <div className="w-full h-full flex flex-col gap-4">
        <div className="w-full h-[50px] flex justify-center items-center sticky top-0 bg-white p-4">
          <img src="/triple_logo.png" alt="logo" className="h-4" />
        </div>
        <div className="w-full flex flex-col justify-center items-center gap-4 p-4">
          <p className="text-lg font-bold">{companyName}</p>
          <CheckIcon className="w-16 h-16 text-accent bg-primary rounded-full" />
          <p className="text-md">従業員登録が完了しました。</p>
          <p className="text-md">このままアプリを閉じてください。</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col gap-4">
      <div className="w-full h-[50px] flex justify-center items-center sticky top-0 bg-white p-4">
        <img src="/triple_logo.png" alt="logo" className="h-4" />
      </div>
      <div className="w-full flex flex-col justify-center p-1">
        <div className="w-full flex flex-col justify-center items-center gap-1">
          <p className="text-lg font-bold">{companyName}</p>
          <p className="text-lg font-bold">従業員登録</p>
        </div>
        <form onSubmit={handleSubmit(submit)} className="flex flex-col gap-4 p-4">
          <div className="flex items-center gap-1">
            <div className="flex flex-col gap-1">
              <Label htmlFor="lastName">
                姓 <span className="text-manual-red">(必須)</span>
              </Label>
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.lastName && (
                <span className="text-manual-red text-xs">{errors.lastName.message}</span>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="firstName">
                名 <span className="text-manual-red">(必須)</span>
              </Label>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.firstName && (
                <span className="text-manual-red text-xs">{errors.firstName.message}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="flex flex-col gap-1">
              <Label htmlFor="lastNameKana">
                姓（フリガナ） <span className="text-manual-red">(必須)</span>
              </Label>
              <Controller
                name="lastNameKana"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.lastNameKana && (
                <span className="text-manual-red text-xs">{errors.lastNameKana.message}</span>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="firstNameKana">
                名（フリガナ） <span className="text-manual-red">(必須)</span>
              </Label>
              <Controller
                name="firstNameKana"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.firstNameKana && (
                <span className="text-manual-red text-xs">{errors.firstNameKana.message}</span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="employmentType">
              雇用形態 <span className="text-manual-red">(必須)</span>
            </Label>
            <Controller
              name="employmentType"
              control={control}
              render={({ field }) => (
                <Select {...field} onValueChange={field.onChange}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="雇用形態を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(EmploymentTypeLabels).map((t) => (
                      <SelectItem key={t} value={t}>
                        {EmploymentTypeLabels[t as EmploymentType]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div className="flex flex-col justify-center gap-1">
            <Label htmlFor="gender">性別</Label>
            <Controller
              name="gender"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  className="flex flex-col justify-center gap-1"
                  {...field}
                  onValueChange={field.onChange}
                >
                  {Object.keys(GenderLabels).map((g) => (
                    <div key={g} className="flex gap-1 p-2">
                      <RadioGroupItem value={g} id={g} />
                      <Label htmlFor={g}>{GenderLabels[g as Gender]}</Label>
                    </div>
                  ))}
                </RadioGroup>
              )}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="birthday">生年月日</Label>
            <Controller
              name="birthday"
              control={control}
              render={({ field }) => (
                <DatePicker
                  className="w-full"
                  {...field}
                  calenderProps={{
                    captionLayout: "dropdown",
                    numberOfMonths: 1,
                  }}
                />
              )}
            />
            {errors.birthday && (
              <span className="text-manual-red text-xs">{errors.birthday.message}</span>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="email">メールアドレス</Label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => <Input {...field} />}
            />
            {errors.email && (
              <span className="text-manual-red text-xs">{errors.email.message}</span>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <div className="flex flex-col gap-1">
                <Label htmlFor="province">都道府県</Label>
                <Controller
                  name="province"
                  control={control}
                  render={({ field }) => (
                    <Select {...field}>
                      <SelectTrigger>
                        <SelectValue placeholder="都道府県を選択" />
                      </SelectTrigger>
                      <SelectContent className="overflow-y-auto max-h-60">
                        {PROVINCES.map((p) => (
                          <SelectItem key={p.name} value={p.value}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="postalCode">郵便番号</Label>
                <Controller
                  name="postCode"
                  control={control}
                  render={({ field }) => <Input placeholder="000-0000" {...field} />}
                />
                {errors.postCode && (
                  <span className="text-manual-red text-xs">{errors.postCode.message}</span>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="city">市町村</Label>
              <Controller
                name="city"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.city && (
                <span className="text-manual-red text-xs">{errors.city.message}</span>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="address">丁目番地号</Label>
              <Controller
                name="address"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.address && (
                <span className="text-manual-red text-xs">{errors.address.message}</span>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="buildingNo">マンション名・部屋番号</Label>
              <Controller
                name="buildingNo"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.buildingNo && (
                <span className="text-manual-red text-xs">{errors.buildingNo.message}</span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="phoneNumber">電話番号</Label>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => <Input placeholder="000-0000-0000" {...field} />}
            />
            {errors.phoneNumber && (
              <span className="text-manual-red text-xs">{errors.phoneNumber.message}</span>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="joiningDate">
              入社日 <span className="text-manual-red">(必須)</span>
            </Label>
            <Controller
              name="joiningDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  className="w-full"
                  {...field}
                  calenderProps={{
                    captionLayout: "dropdown",
                    numberOfMonths: 1,
                  }}
                />
              )}
            />
            {errors.joiningDate && (
              <span className="text-manual-red text-xs">{errors.joiningDate.message}</span>
            )}
          </div>
          <div className="flex flex-col gap-1 sticky bottom-0 bg-white p-2">
            <Button
              variant="outline"
              className="rounded-full bg-gradient-to-r from-manual-from to-manual-to"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-1">
                  <LoaderIcon className="h-5 w-5 animate-spin" />
                  <span>登録中</span>
                </div>
              ) : (
                <span className="font-bold">登録</span>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function EmployeeSignupLoading() {
  return (
    <div className="w-full h-full flex flex-col gap-4">
      <div className="w-full h-[50px] flex justify-center items-center sticky top-0 bg-white p-4">
        <img src="/triple_logo.png" alt="logo" className="h-4" />
      </div>
      <div className="w-full flex flex-col justify-center items-center gap-4 p-4">
        <LoaderIcon className="w-16 h-16 animate-spin text-[#00a080]" />
        <p className="text-md">読み込み中です。</p>
      </div>
    </div>
  )
}
